import type { ReactNode } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { useI18n } from '@/i18n/I18nProvider'
import { localePath, routeForPath } from './routes'
import { navLocale, localizeHref } from '@/i18n/href'
import { BUILD_YEAR } from '@/lib/site'
import { cn } from '@/lib/cn'
import LanguageSwitcher, { type Alternate } from '@/components/LanguageSwitcher'
import PreviewBanner from '@/components/PreviewBanner'
import ContactBand from '@/components/ContactBand'
import logos from '@/content/logos.json'
import photos from '@/content/photos.json'

/** Same manifest `SiteLogo` reads; the footer only needs the resolved variant. */
type LogoManifest = Record<string, { width: number; height: number; fallback: string }>

/**
 * The two decorative gradient ribbons, resolved out of the photo manifest.
 *
 * Read here rather than spelled out as `/photo/gradient-ribbon-loop-1000.webp`,
 * because `pnpm images` names every variant after the width it actually
 * produced — replace the source at a different size and a hand-written path
 * breaks silently. A missing entry renders no ribbon at all, which is the right
 * failure for something purely decorative.
 */
type PhotoManifest = Record<string, { width: number; height: number; fallback: string }>
const ribbonLoop = (photos as PhotoManifest)['/photo/gradient-ribbon-loop.png']
/**
 * The footer arc. `gradient-ribbon-pink-arc.png` since 2026-09-11 — see the
 * long note beside it in the footer: it is the same motif drawn *whole*, both
 * legs running out through the bottom of its own canvas, where
 * `gradient-ribbon-arc.png` ends each leg with a flat stroke cap inside the
 * picture. Same 1000×800 source, so every size number below is unchanged.
 */
const ribbonArc = (photos as PhotoManifest)['/photo/gradient-ribbon-pink-arc.png']

/**
 * The loop, drawn once, by the single layer in `Layout`. It was briefly drawn
 * twice — a second copy clipped to the header band, so the shape crossed the
 * bar — until 2026-08-27 asked for the bar to stay on top; kept as its own
 * element because that is what made the two copies impossible to drift apart.
 *
 * Sizes are the whole dial. The lineage is 540 / 980 at `-right-24` /
 * `-right-40`, then ×1.5 to 810 / 1470 at `-right-36` / `-right-60`, then ×0.8
 * back down to today's 650 / 1175 at `-right-28` / `-right-48` ("biraz
 * küçültelim", 2026-08-26) — still bigger than it started, a fifth smaller than
 * the 1.5× round. The offset scales with the width every time, on purpose:
 * bigger or smaller means the whole composition, bleed included, not a
 * differently-sized shape pinned to the same corner. At 1175 with `-right-48`
 * the shape's left edge lands ~985px in from the right, so on a 1920 screen it
 * crosses the top of the page around x≈1050 and runs off the right edge on its
 * way down. Smaller and pulled in below `md`, so a phone does not get a 1175px
 * graphic laid across it.
 */
/**
 * The two pages the closing contact band does NOT go on.
 *
 * Contact Us, since 2026-08-26 — "contact us sayfasında alttaki design a gerek
 * yok". A card whose whole content is a button back to the page you are already
 * reading is noise, and on this page it also sits directly under the offices and
 * the form, so it offers a third route to the same thing. That reasoning got
 * stronger on 2026-08-30, when the band became the form: it would now be the
 * same form twice on one document.
 *
 * The home page joined it the same day, asked for in the same breath ("main page
 * ve contact us sayfamız dışındaki her sayfada"). It closes with its own `form`
 * section — DISCOVER. DEPLOY. OUTCOME. over five fields — and that section is
 * the page's ending, not a duplicate to be replaced. Its slug is the empty
 * string, because `pageSlug()` strips the locale prefix and `/en/` leaves
 * nothing behind.
 *
 * The Partner Program page opted out on 2026-08-30, for the same reason and by
 * the same route: it now closes with its own `form` section, because the three
 * partnership models have to be tickable in it and those questions belong to
 * that page rather than to every document on the site.
 *
 * Matched on the trailing slug rather than the full path so it holds for any
 * locale prefix the page ever gets, and with the trailing slash tolerated
 * because the prerenderer writes directories. `useLocation()` is safe on the
 * imported pages even though they never hydrate: `prerender.tsx` renders them
 * inside a `StaticRouter` at their own URL, so this is decided at build time and
 * the band is simply absent from the HTML.
 */
const NO_CONTACT_BAND: ReadonlySet<string> = new Set([
  '',
  'contact-us',
  'partner-program',
  'solutions',
  // Careers (2026-09-07): a page addressed to candidates ended in a form asking
  // for Business Email, Company Name and Industry — the sales form, under a
  // list of jobs. It closes with its own `cta` band now, pointing at Contact Us
  // for the person whose role is not listed yet.
  'become-a-digitopian',
])

function suppressesContactBand(pathname: string): boolean {
  const slug = pageSlug(pathname)
  // A careers role page (2026-09-07) ends in its own `apply` section — the
  // application form — and a contact form under an application form would be
  // the wrong form, asked twice. The listing page is in the set above.
  if (slug.startsWith('become-a-digitopian/')) return true
  return NO_CONTACT_BAND.has(slug)
}

/** The trailing slug, locale prefix and trailing slash removed. */
function pageSlug(pathname: string): string {
  return pathname.replace(/\/+$/, '').split('/').slice(2).join('/')
}

/**
 * The pages the loop at the top of the document does NOT go on.
 *
 * Asked for on 2026-08-28 — "hall of fame sayfasında yukarıdan inen ribbon
 * olmasın". The Hall of Fame is a single column of long quote cards on plain
 * white, so the shape descending from the top right runs straight down behind
 * the first two of them rather than behind a hero with its own background,
 * which is where it was designed to sit.
 *
 * Customers joined it on 2026-08-28 — "bu sayfada üstten gelen ribbon olmasın",
 * asked for in the same breath as swapping that page's hero for the full-size
 * original. Its band is the same construction as the Hall of Fame's and the
 * feathered edge takes the photograph out to white on every side, so a coloured
 * shape descending through it is the one thing with a hard edge in a hero built
 * to have none.
 *
 * The Transform Better e-book page joined on 2026-08-31 — "/transform-better-e-book
 * sayfasında üstten ribbon inmesin aşağıya doğru". Its opening band is the
 * reference page's own photograph under a scrim, and the shape carries on past
 * it into the centred standfirst on white, which is the part it is seen against.
 *
 * Matched on the slug the same way `NO_CONTACT_BAND` is, so it holds under any
 * locale prefix, and the arc in the footer is untouched — only the one that
 * comes down from the top was asked about.
 */
const NO_TOP_RIBBON: ReadonlySet<string> = new Set([
  'testimonials',
  'customers',
  'impact-labs/ebooks/transform-better-e-book',
])

function suppressesTopRibbon(pathname: string): boolean {
  return NO_TOP_RIBBON.has(pageSlug(pathname))
}

const loopImage = ribbonLoop ? (
  <img
    src={ribbonLoop.fallback}
    alt=""
    width={ribbonLoop.width}
    height={ribbonLoop.height}
    decoding="async"
    className="absolute -right-28 top-0 w-[650px] max-w-none md:-right-48 md:w-[1175px]"
  />
) : null

/**
 * The chrome every page wears: preview stripe, header, footer.
 *
 * Nav items come from `ROUTES` filtered by `inNav`, with labels out of the
 * dictionary — so a page cannot appear in the nav without copy in the current
 * locale, and adding one is still a single registry entry.
 *
 * Takes `children` as well as rendering `<Outlet/>`, because it dresses two kinds
 * of page: the routed, hydrated ones the client app owns, and the imported legacy
 * pages, which are rendered once at build time outside the router's route tree
 * (see `src/legacy.ts`). One chrome, both paths.
 */
export default function Layout({
  children,
  alternates,
}: {
  children?: ReactNode
  /**
   * The languages this document actually exists in. Omitted by the routed pages,
   * which exist in every locale; supplied by the imported ones, which do not.
   */
  alternates?: Alternate[]
}) {
  const { locale, t } = useI18n()
  const { pathname } = useLocation()
  // A routed page written in only some locales (`RouteDef.locales`) offers only
  // those, exactly as an imported page does; one locale hides the switcher.
  const routed = alternates ? undefined : routeForPath(pathname.split('/').filter(Boolean).slice(1).join('/'))
  const languages: Alternate[] | undefined =
    alternates ??
    routed?.locales?.map((code) => ({ locale: code, href: localePath(code, routed.path) }))
  const showContactBand = !suppressesContactBand(pathname)
  const showTopRibbon = !suppressesTopRibbon(pathname)

  return (
    <div className="relative isolate flex min-h-dvh flex-col">
      <PreviewBanner />

      {/* Ribbon one of two, and the reason this wrapper is now `relative
          isolate`: the
          loop, at the very top of every page, entering from the top right and
          running off the right edge on its way down (asked for 2026-08-26,
          pointing at how digitopia.co carries it). Its twin is at the bottom
          left, inside the footer.

          It is a sibling of the header rather than a child of `<main>`, which
          is where the ribbon that was removed on 2026-08-26 used to live: `main`
          starts *below* the 66px header, and "everything's very top" means the
          top of the document. So the first 66px of the shape sits *behind* the
          bar — the shape enters from off the top of the page and emerges from
          under the header, which is how it reads on the page.

          The clipping wrapper is what makes "off the page" free of a horizontal
          scrollbar. It cannot be `overflow-hidden` on the flex column itself:
          that would turn this element into the header's scroll container and
          `position: sticky` would quietly stop working. So the overflow is
          clipped by a separate absolutely-positioned box that is nobody's
          ancestor.

          `isolate` on the wrapper and `-z-10` here are what put it behind the
          page rather than over it, and the pair matters: a negative-z child
          paints above its stacking context's own background but below every
          in-flow background inside it. Without the `isolate` the ribbon would
          be a positioned `z-0` box, and positioned boxes paint *after* in-flow
          backgrounds — it would have been drawn on top of the preview stripe.
          Body's `bg-background` propagates to the canvas, which is painted
          below the stacking context entirely, so the ribbon is still visible.
          Sections with their own background cover it exactly the way they do on
          the live site; it shows through the white ones.

          **It was briefly drawn twice, and the second copy is gone again**
          (2026-08-26 asked for "üst tab'ın da üstüne olsun"; 2026-08-27 asked
          for the opposite — "üstteki tab her zaman ribbon'un üstünde olsun,
          ribbon tab'ın üstünde olunca çok güzel olmadı"). That second copy was
          a 66px slice of the same image at `z-50`, clipped to the header band,
          which is what put the ribbon over the bar. One layer cannot be both:
          the header is `z-40` over an opaque background, so a single ribbon is
          either behind the bar or in front of it *and* in front of the hero
          photograph, because `main` is `isolate` and nothing inside it can
          climb over a `z-50` sibling. With the slice removed there is one
          layer, at `-z-10`, and the bar is opaque above it — so the header wins
          at the top of the page and everywhere down it, which is what "always"
          asked for. Bringing it back means restoring a second
          `absolute inset-x-0 top-0 z-50 h-[66px] overflow-hidden` copy here,
          and nothing else.

          The wrapper is `relative h-0`, an in-flow flex item of no height
          sitting immediately above the header — that is what makes `top-0` mean
          *the top of the header* rather than the top of the document. It
          matters in preview builds only, where `PreviewBanner` adds an opaque
          stripe above the header: anchored to the document the shape would
          start behind the stripe instead of at the bar. `h-0` with
          `z-index: auto` is deliberately not a stacking context, so the `-z-10`
          below resolves against the page.

          **The window is sized to the artwork, and that is what stopped it
          being cut** (2026-09-07, "hala ribbon kesiliyor"). The source is
          1000×800, so drawn at 650 / 1175 wide it stands 520 / 940 tall —
          while this window was 505 / 910, clipping the last 15 / 30px of a
          shape whose faint tail runs all the way to the bottom of its own
          canvas. A straight horizontal edge across a dissolving gradient is
          the cut being pointed at, and 910px down the document it lands in the
          footer on a short page. Same rule the footer arc was put back on
          earlier the same day: hold height = width × 0.8 on both breakpoints
          on any future round, so resizing the loop never re-crops it. */}
      {loopImage && showTopRibbon && (
        <div aria-hidden className="relative h-0">
          <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[520px] overflow-hidden md:h-[940px]">
            {loopImage}
          </div>
        </div>
      )}

      {/* Keyboard users reach this first; it is visually hidden until focused. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-3 focus:rounded-md focus:bg-card focus:px-4 focus:py-2 focus:shadow-[var(--shadow-md)]"
      >
        {t.nav.skipToContent}
      </a>

      {/* `header-scroll-shadow` is the one non-utility class here: it fades a
          soft edge in under the bar over the first 72px of scroll and leaves it
          flat at the top of the page. It lives in `index.css` because it rides
          a scroll-driven animation timeline — keyframes plus an `@supports`
          gate, which is not something a class list on this element can say. */}
      <header className="header-scroll-shadow sticky top-0 z-40 bg-background">
        <div className="site-container flex h-[66px] items-center gap-8">
          {/* The mark has linked to the locale's home page since the header was
              built, and it is a plain `<a href>` in the HTML — the `<Link>`
              renders one under `StaticRouter` too, which it has to, since 167 of
              the built documents ship no bundle. What was missing was somewhere
              to *press* on a phone (2026-09-17, a screenshot of the phone
              header: "mobilde bu logoya tıkladığımızda ana sayfaya gidelim").
              The anchor was only as tall as the artwork — a 35px strip centred
              in a 66px bar — so the ~15px of apparent logo above and below it
              was not the link, and a thumb aimed at a thin wordmark lands there
              about as often as on it.

              `flex h-full items-center` makes the anchor the full height of the
              bar, so the whole logo area is the target, and the ~15px of empty
              box the 149×35 frame already leaves to the right of the mark comes
              with it. Nothing moves by a pixel: the img keeps its own box and is
              still centred in the bar, exactly where `items-center` on the row
              put it. CSS only, on purpose — the pages a phone lands on from
              search are the ones that never hydrate. */}
          <Link
            to={localePath(navLocale(locale), '')}
            className="flex h-full shrink-0 items-center"
            aria-label={t.site.name}
          >
            {/* 149×35 — the box the live site gives its logo, asked for by
                that exact pair on 2026-08-26.

                Lineage: 149, then 134×35, then 134.1×31.5, then 147.5×34.65,
                then back to 134.1×31.5, and now the requested pair again.
                Every earlier round pinned both axes with `w-[…] h-[…]` and no
                `object-fit`, which is why the mark kept coming back stretched:
                149/35 is 4.26 against the file's own 187/49 = 3.82, so forcing
                both axes renders the wordmark ~11% too wide and it reads as
                squashed flat next to the live site's.

                `object-contain object-left` is what breaks that cycle. The box
                really is 149×35 — the attributes and the CSS agree, so the
                pre-CSS layout hint reserves exactly what renders — while the
                mark inside it keeps the file's own ratio, drawing 35px tall and
                ~134px wide, flush left, with the ~15px difference falling as
                empty space before the nav. Requested size, no distortion. If a
                future round wants the mark itself wider, the answer is a logo
                file at the 4.26 ratio, not a bigger `w-[…]`. */}
            <img
              src="/digitopia-logo.png"
              alt={t.site.name}
              width={149}
              height={35}
              className="h-[35px] w-[149px] object-contain object-left"
            />
          </Link>

          {/* Curated in the dictionary rather than derived from the route
              registry: three of the five point at imported legacy pages, and
              deriving the nav from "pages we designed" would silently drop
              Customers and Resources.

              Each item opens its own panel on hover. **Pure CSS — `group-hover`
              and `group-focus-within`, no state and no handler** — because 117
              of this site's documents are imported pages that ship with no
              JavaScript at all (see `src/legacy.ts`). A React-driven dropdown
              would render there and never open, which is exactly the wrong half
              of the site for the header to stop working on.

              `group-focus-within` is what makes it reachable from the keyboard:
              focusing the top-level link reveals the panel, so the next Tab
              lands inside it rather than skipping past. A hidden panel is
              `invisible`, not merely transparent, so it is out of the
              accessibility tree and out of the tab order until it opens.

              **Where the hover is detected is not the box the panel hangs from**
              (asked for 2026-08-26: the panel opened while the pointer was merely
              *near* the word, not on it). This `div` is a full-height flex item —
              66px against ~20px of text — so the empty ~23px above and below the
              label was opening the menu. It is now `pointer-events-none`, which
              takes that empty column out of hit testing while leaving the geometry
              untouched: `top-full` still means the bottom of the header, so
              nothing moved by a pixel. The three things that *should* hold the
              panel open opt back in with `pointer-events-auto` — the label, the
              bridge under it, and the panel wrapper. That last one is the half of
              the request that was explicit: without it the panel would close the
              instant the pointer entered it.

              A parent with `pointer-events: none` still receives `:hover` from a
              descendant that opts back in, which is why `group-hover` keeps
              working with no state and no handler. */}
          <nav className="hidden h-full items-stretch gap-7 sm:flex">
            {t.nav.items.map((item, i) => (
              <div key={item.href} className="group pointer-events-none relative flex items-center">
                {/* 1.045rem (16.7px) — 0.95rem grown by the same 10% as the
                    logo beside it, asked for together (2026-08-26). Revert =
                    `text-[0.95rem]`. Only these five top-level items moved; the
                    entries inside the dropdown panels are still 0.95rem. The
                    logo was later asked back down to 134.1×31.5 without a word
                    about this size, so the 10% here deliberately stayed.
                    `font-bold` (700) asked for on all five in the same breath
                    (2026-08-26) — it matches the panel headings and the entries
                    inside them, which were already bold. The colour fades over
                    the same 200ms as the panel below it: it is one hover, and it
                    carried the same `transition-duration: var(--transition-base)`
                    bug (see the panel's comment — that substitution computes to
                    0s), so the word used to snap to indigo while the panel now
                    fades. */}
                <a
                  href={localizeHref(item.href, locale)}
                  className="pointer-events-auto relative text-[1.045rem] font-bold transition-colors duration-200 ease-out hover:text-primary group-hover:text-primary group-focus-within:text-primary"
                >
                  {item.label}
                  {/* The bridge from the label down to the panel. Once the
                      column above and below stopped being a hover target, the
                      ~23px between the bottom of the text and the bottom of the
                      header became a dead band the pointer crossed on its way to
                      the panel — the menu would flicker shut halfway down. This
                      span is a child of the label, so it inherits its
                      `pointer-events-auto`, and it only reaches *downward*:
                      hovering above the word still does nothing, which is the
                      point. 24px covers the 23px band with a hair to spare and
                      stops inside the wrapper's own 8px `pt-2`, so it never
                      overlays the page below the header. Its width is the label's
                      — `inset-x-0` — so it is not a wider target than the word,
                      it is a taller one. */}
                  <span aria-hidden className="absolute inset-x-0 top-full h-6" />
                </a>

                {/* Anchored to the bottom of the header, and the padding is the
                    hover bridge: a margin would be a dead gap the pointer falls
                    through on its way down to the panel. The last two panels
                    hang from their right edge so a 32rem card cannot push past
                    the right of the viewport.

                    **It opens on a 200ms fade and a 4px rise** (asked for
                    2026-08-26: "bir anda ortaya çıkıyor … smooth bir şekilde
                    ortaya çıkışını görelim"). It read as instant because it
                    genuinely was: the class here used to be
                    `[transition-duration:var(--transition-base)]`, and
                    `--transition-base` is `150ms cubic-bezier(0.4, 0, 0.2, 1)` —
                    a whole shorthand, not a time. A `var()` that substitutes to
                    something the property cannot accept is *invalid at
                    computed-value time*, which does not drop the declaration; it
                    resets the property to its initial value. For
                    `transition-duration` that initial value is **0s**, so the
                    panel had no transition at all. Same trap sits on the label
                    above and on several other elements in this repo — that token
                    belongs in a `transition:` shorthand, never in
                    `transition-duration`. Use plain `duration-*` utilities here.

                    `visibility` is in the transition list on purpose. It is the
                    one discrete property with a special interpolation rule — any
                    progress above zero counts as `visible` — so the panel is
                    shown for the whole of the opening fade *and* the whole of the
                    closing one, flipping back to hidden only at the very end.
                    That keeps the accessibility and tab-order behaviour the
                    `invisible` class was there for while letting the close fade
                    too, instead of snapping away mid-fade. */}
                <div
                  className={cn(
                    'pointer-events-auto invisible absolute top-full z-50 w-[32rem] translate-y-1 pt-2 opacity-0',
                    'transition-[opacity,transform,visibility] duration-200 ease-out',
                    'group-hover:visible group-hover:translate-y-0 group-hover:opacity-100',
                    'group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100',
                    i >= t.nav.items.length - 2 ? 'right-0' : 'left-0',
                  )}
                >
                  {/* The soft drop shadow asked for in the same breath ("hafif
                      bir gölgesi olsun"). It was on `--shadow-md`, which is tuned
                      for a card resting *in* the page — 12px of blur at 6% ink,
                      invisible under a panel that floats over one. Same ink as
                      the token family (rgba(14, 17, 23, …)), just lifted: a wide,
                      soft 30px pool plus a tight 6px contact shadow, so the panel
                      reads as hovering without drawing a grey band. Quieter =
                      lower the two alphas; that is the whole dial. */}
                  <div className="flex gap-8 rounded-[--radius] border border-border bg-card p-7 shadow-[0_10px_30px_-6px_rgba(14,17,23,0.12),0_2px_6px_rgba(14,17,23,0.05)]">
                    <div className="w-44 shrink-0 border-r border-border pr-8">
                      <p className="text-lg font-bold tracking-tight">{item.label}</p>
                      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                        {item.blurb}
                      </p>
                    </div>
                    <ul
                      className={cn(
                        'min-w-0 flex-1 space-y-4',
                        // A single-entry panel (Partners, today) reads as
                        // half-empty left-aligned at the top of a column as
                        // tall as its neighbour's blurb text. Centered inside
                        // its own area instead — asked for 2026-09-11.
                        item.menu.length === 1 && 'flex flex-col items-center justify-center text-center',
                      )}
                    >
                      {item.menu.map((entry) => (
                        <li key={entry.label}>
                          <a href={localizeHref(entry.href, locale)} className="group/entry block">
                            <span className="text-[0.95rem] font-bold group-hover/entry:text-primary">
                              {entry.label}
                            </span>
                            <span className="mt-0.5 block text-sm text-muted-foreground">
                              {entry.description}
                            </span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-5">
            <LanguageSwitcher className="hidden sm:block" alternates={languages} />
            {/* A plain text link to the platform, then the one filled action —
                the arrangement the live site's header uses. */}
            <a
              href={t.nav.loginHref}
              className="hidden text-[0.95rem] font-medium text-primary hover:underline sm:inline"
            >
              {t.nav.login}
            </a>
            <a
              href={localizeHref(t.nav.cta.href, locale)}
              className="inline-flex items-center rounded-[--radius-pill] bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground transition-colors [transition-duration:var(--transition-base)] hover:brightness-95"
            >
              {t.nav.cta.label}
            </a>

            {/* THE MOBILE NAV. Below `sm` the bar above is `hidden`, and until
                2026-09-15 nothing replaced it — a phone got the logo and the
                Contact Us pill and no way to reach any of the five sections
                except through the footer's link columns ("mobil versiyonda
                navigasyon gözükmüyor").

                `<details>` rather than a button with state, for the reason the
                dropdowns above and `LanguageSwitcher` are both built this way:
                117 of the built documents are imported pages that ship with no
                client bundle, so a React `onClick` here would be a hamburger
                that does nothing on most of the site. The browser opens and
                closes this one itself, and the links inside it are in the HTML
                either way. Every entry is a real `<a>` doing a full navigation,
                so the panel closes by the page being replaced — there is no
                open state to reset.

                Flat rather than nested: each top-level item is its own link,
                with its panel's entries indented underneath. Five sections and
                ~20 entries scroll comfortably, and a second layer of taps to
                reach a page that is already listed is the thing a phone menu
                usually gets wrong.

                The panel hangs off the header, which is `sticky` and therefore
                the positioning origin — `top-full` is the bottom of the 66px
                bar at whatever scroll position it is stuck at. `max-h` leaves
                that bar visible above a menu longer than the screen, and the
                descriptions from the desktop panels are dropped: the labels
                alone are what a narrow column can carry. */}
            <details className="group sm:hidden">
              <summary
                aria-label={t.nav.menu}
                className="flex h-10 w-10 cursor-pointer list-none items-center justify-center rounded-[--radius] border border-border [&::-webkit-details-marker]:hidden"
              >
                {/* Three bars that become a cross. The box is 14px tall with
                    2px bars at 0 / 6 / 12, so its own centre line is 7 — which
                    is why the outer two travel exactly 6px to meet there before
                    they rotate. Move the box height and those two numbers move
                    with it, or the cross stops closing. */}
                <span aria-hidden className="relative block h-[14px] w-5">
                  <span className="absolute inset-x-0 top-0 h-0.5 rounded-full bg-foreground transition-transform duration-200 ease-out group-open:translate-y-[6px] group-open:rotate-45" />
                  <span className="absolute inset-x-0 top-1.5 h-0.5 rounded-full bg-foreground transition-opacity duration-200 ease-out group-open:opacity-0" />
                  <span className="absolute inset-x-0 top-3 h-0.5 rounded-full bg-foreground transition-transform duration-200 ease-out group-open:-translate-y-[6px] group-open:-rotate-45" />
                </span>
              </summary>

              <div className="absolute inset-x-0 top-full max-h-[calc(100dvh-66px)] overflow-y-auto border-t border-border bg-background shadow-[0_10px_30px_-6px_rgba(14,17,23,0.12)]">
                <nav className="site-container flex flex-col gap-6 py-6">
                  {t.nav.items.map((item) => (
                    <div key={item.href}>
                      <a
                        href={localizeHref(item.href, locale)}
                        className="block text-base font-bold hover:text-primary"
                      >
                        {item.label}
                      </a>
                      <ul className="mt-3 space-y-3 border-l border-border pl-4">
                        {item.menu.map((entry) => (
                          <li key={entry.label}>
                            <a
                              href={localizeHref(entry.href, locale)}
                              className="block text-[0.95rem] text-muted-foreground hover:text-primary"
                            >
                              {entry.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}

                  <div className="flex flex-wrap items-center justify-between gap-4 border-t border-border pt-6">
                    <a
                      href={t.nav.loginHref}
                      className="text-[0.95rem] font-medium text-primary hover:underline"
                    >
                      {t.nav.login}
                    </a>
                    {/* `up`, like the footer's: this row is the last thing in a
                        panel that scrolls and ends at the bottom of the screen,
                        so a list opening downward lands below the panel's edge
                        and is clipped by it. Halil hit exactly that on his
                        phone on 2026-09-25; `pnpm check:phone` now opens it. */}
                    <LanguageSwitcher alternates={languages} direction="up" />
                  </div>
                </nav>
              </div>
            </details>
          </div>
        </div>
      </header>

      {/* The gradient ribbon that used to sit behind the top of *this* element
          was removed on 2026-08-26 ("arka plana koyduğumuz ribbonu kaldıralım")
          and its replacement, asked for later the same day, deliberately does
          not live here — see the two comments above and in the footer. `main`
          begins below the header, so a ribbon inside it can never start at the
          top of the page.

          `relative isolate` stays: it is the stacking context and positioning
          origin any page-level layer measures itself against, and dropping it
          would silently move things that have nothing to do with the ribbon.
          `isolate` is also what keeps a page's own `-z-*` layer from punching
          through to the ribbon behind.

          `z-10` is the same fix `ContactBand` carries, for the page that has no
          band. The footer's arc is `-z-10` inside the footer, and the footer is
          a *later positioned sibling* of `main` — so its whole stacking context
          paints over this one, and no z-index on a section inside `main` can
          answer that ("homepagedeki ribbon liveda formun üstüne çıkıyor",
          2026-09-03: the arc was drawn across the home page's own contact form,
          which is the last section of `main` because home opts out of the band).
          Ten beats the footer's `auto` and stays well under the sticky header's
          `z-40`, so the bar still wins at the top of the page. It changes
          nothing elsewhere: `main` has no background, so the two ribbons still
          show through every transparent section exactly as before. */}
      <main id="main" className="relative isolate z-10 flex-1">
        {children ?? <Outlet />}
      </main>

      {/* The closing call to action, asked for on 2026-08-26 with a reference
          design: "her sayfanın altında olacak contact us kısmı".

          A sibling of `main` rather than a section a page opts into, for the
          same reason the footer is: "every page" here means all 154 documents,
          and 117 of them are imported WordPress pages that have no `sections`
          array to add anything to. One chrome, both paths — so it lands on the
          written pages, the blog and the imported ones alike.

          Every page but Contact Us, since 2026-08-26 — see `NO_CONTACT_BAND`
          above for why one page opts out and how. */}
      {showContactBand && <ContactBand />}

      <footer className="relative isolate bg-band">
        {/* Ribbon two of two: the arc, rising out of the bottom-left corner of
            the page (asked for 2026-08-26 — "sol altından sıfırdan çıkıp
            gelsin"). `left-0 bottom-0` with no offsets is still that corner,
            though the artwork now fades out shortly before it reaches either
            edge rather than running off them (see the sizing note below).

            It lives in the footer because the footer *is* the bottom of every
            document — including the 117 imported pages, which this same chrome
            dresses. `bg-band` is opaque #f5f5f5, so a layer further out would
            simply be covered by it.

            `isolate` + `-z-10` is the pairing that puts it between the two: a
            stacking context's negative-z children paint above that element's
            own background and below its in-flow content, so the ribbon sits on
            the band and under the footer's links without any of them needing a
            z-index of their own.

            **The artwork was replaced on 2026-08-26 and the width dial moved
            with it, on purpose** ("aşağıdan gelen ribbon u da bunun ile
            değiştirelim ancak boyut şu anki gibi olsun"). Both the old and the
            new `gradient-ribbon-arc.png` are 1000×800, but the new one draws a
            markedly bolder, taller curve inside that canvas: its stroke is
            ~55px against ~45px and it spans the full height where the old one
            filled only the bottom ~60%. Left at 630 / 960 the swap alone would
            have rendered the ribbon about a quarter bigger, which is the one
            thing the request ruled out — so the widths came down ~22% to 490 /
            750, which puts the drawn curve back at roughly the size it was on
            the page. **"Same size" here means what the reader sees, not the
            numbers in this class list**; the numbers had to move for the
            picture to stay put.

            The window is sized to the image (1125 × 0.8 = 900) rather than left
            oversized, because this artwork reaches the top edge of its own
            canvas: an overhanging wrapper used to be harmless when the top 39%
            of the source was empty, but drawn pixels up there would be painted
            over the contact band's card — the footer is a later positioned
            sibling, so its stacking context wins.

            **Every number here went ×1.5 on 2026-08-26** ("aşağıdan gelen
            ribbon 1.5 kat daha büyük olsun"): 490 / 750 wide became 735 / 1125,
            and the two windows moved with them so the curve is still drawn
            whole rather than cropped by a straight line across its top. The
            window and the width scale together on purpose — the same rule the
            loop at the top of the page follows — because "bigger" means the
            whole composition, not a bigger picture inside the old opening.

            **Then ×1.2 again on 2026-08-27** ("alttan gelen ribbonu 0.2x
            büyütelim", read as one fifth bigger rather than one fifth of the
            size): 735 / 1125 became 882 / 1350, and 593 / 908 became 712 /
            1090. Same rule as the round before — every number moved by the
            same factor, so the curve grows without being re-cropped.

            **And ×0.8 later the same day** ("alttan gelen ribbon 0.2x
            küçülsün" — the mirror of the request above, so a fifth smaller and
            not a fifth of the size): 882 / 1350 became 706 / 1080, and 712 /
            1090 became 570 / 872. The 0.807 window-to-width ratio is what
            keeps the arc drawn whole; hold it on any future round.

            The desktop window is still a touch taller than the footer itself,
            so the top of the arc rises past the footer's edge and into the
            contact band. That is intended, and it is why the card up there
            carries `z-10`: the arc passes *behind* it and emerges from under
            its bottom edge, instead of being painted across the headline.
            Shrink this much further and that class is simply idle.

            **The image hangs below the window on purpose since 2026-08-28**
            ("ribbon burada sağdaki yere takılmış o göze batıyor… tam sayfanın
            altından çıkıyor gibi"). The artwork's descending right tail fades
            to nothing at about 84% of the canvas height, so with the image
            flush at `bottom-0` that dissolve finished ~140px *above* the page
            edge and the ribbon appeared to stop in mid-air over the copyright
            line. Dropping the image by roughly that empty strip — 110px on
            mobile, 170px on desktop, the same 0.654 ratio as the two widths —
            moves the vanishing point past the bottom of the document, so the
            tail is still faint where the page ends and reads as rising out of
            it. The wrapper's `overflow-hidden` does the cut, and the wrapper's
            bottom *is* the bottom of every document, since the footer is the
            last element in the layout.

            The whole arc travels with it, so the crest sits that much lower in
            the band than it did before this round — the alternative was
            scaling the artwork up, and the size is a dial somebody has already
            tuned three times. If the crest needs to come back up, grow the
            window and the width together (holding 0.807) rather than reaching
            for these two offsets.

            **The drop below the window, and the alpha mask that hid the row it
            cut, are both gone since 2026-09-07** — two rounds of fading
            (`…#000_40%,#000c_62%,#0008_78%…`) still read as a cut, and the
            answer given was to stop tuning the fade at all. The two were a
            pair: dropping the artwork past the wrapper's bottom edge is what
            put a coloured row under the `overflow-hidden` cut, and the mask was
            there to hide that row. Remove the drop and there is nothing to
            hide, because the wrapper is sized to the artwork (872 = 1080 ×
            0.8, 570 = 706 × 0.8) — so at `bottom-0` the whole curve is drawn
            and **no edge crosses it anywhere**. What it costs is the 2026-08-28
            round: the tail's own dissolve finishes ~140px above the page edge
            again, so the arc ends inside the footer rather than appearing to
            rise out of the very bottom of the document. That is the trade, and
            a clean end beats a straight line across the artwork. If the tail
            must reach the page edge again, move the *artwork* — a source whose
            colour runs to the bottom of its canvas — not the offsets, and
            never a mask.

            `overflow-hidden` stays: at 1080px the image is wider than a phone
            and the clip is what keeps it off the horizontal scrollbar.

            **The straight line was in the picture, and the answer was a
            different picture** (2026-09-11 — "ribbonın tam halini görmemiz
            gerekirken çizgi şeklinde kesiliyor", pointing at the tail directly
            above the Terms of Service link, then "bence sorun katmanlarla
            alakalı"). It is not layers: `isolate` + `-z-10` paints the arc
            above the band's own background and the footer's dividers are gone,
            so nothing in the layout crosses it. The cut was
            `gradient-ribbon-arc.png` itself — its descending tail is a
            *stroke*, and a stroke ends with a flat cap, ~130px above the
            bottom of the page, with a second flat cap where its left leg stops
            in mid-air at 37% of the canvas. Two ends inside the frame, on a
            shape whose whole reading is "rises out of nothing".

            So the source changed to `gradient-ribbon-pink-arc.png`: the same
            motif, the same 1000×800 canvas and the same brand gradient, drawn
            as a complete arch whose **both legs run out through the bottom
            edge of the canvas**. There is no cap to hide, at either end — the
            only thing that ends the ribbon is the bottom of the document,
            which is the 2026-08-28 reading ("tam sayfanın altından çıkıyor
            gibi") arrived at through the artwork instead of through offsets.
            That is exactly what the note below prescribed when the fades came
            out, and it is why **the mask is gone and must not come back**: it
            was the third attempt at hiding a cap, and it only got the cap to
            ~44% alpha, which is why the round before this one read as no
            change at all.

            What it costs: this arc crests at 34% of its canvas rather than at
            the very top, so with the window unchanged the summit now sits
            ~570px above the bottom of the page instead of ~870px — a flatter,
            lower arch in the band. Every size number here is untouched, so if
            the crest should come back up, grow the window and the width
            together holding 0.807 (×1.22 puts it back) rather than reaching
            for offsets or a mask. */}
        {ribbonArc && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-[570px] overflow-hidden md:h-[872px]"
          >
            <img
              src={ribbonArc.fallback}
              alt=""
              width={ribbonArc.width}
              height={ribbonArc.height}
              loading="lazy"
              decoding="async"
              className="absolute bottom-0 left-0 w-[706px] max-w-none md:w-[1080px]"
            />
          </div>
        )}

        <div className="site-container flex flex-col gap-12 py-16">
          {/* One row from lg up, whatever the locale carries: en has six
              columns, tr five (no Tools). A fixed lg:grid-cols-5 pushed en's
              About onto a second row (QA F-06, 2026-09-29); a fixed 6 would
              leave tr an empty sixth slot. Literal classes, so Tailwind sees them. */}
          <div
            className={`grid gap-10 sm:grid-cols-2 ${
              t.footer.columns.length >= 6 ? 'lg:grid-cols-6' : 'lg:grid-cols-5'
            }`}
          >
            {t.footer.columns.map((col) => (
              <div key={col.title}>
                <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  {col.title}
                </h2>
                <ul className="mt-4 space-y-2">
                  {col.links.map((link) => (
                    <li key={link.href}>
                      <a href={localizeHref(link.href, locale)} className="text-sm hover:text-primary">
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/*
            THE NEWSLETTER BOX: MARKUP HERE, BEHAVIOUR IN `index.html`.
            The split is the same one the cookie bar above already needed, and
            for the same measured reason — **167 of the 248 built documents
            never hydrate.** They carry the footer (it is server-rendered) and
            no module script at all, so an `onSubmit` written in React would be
            a dead form on two thirds of the site, including every imported page
            and every blog post. The markup reaches all of them from here; the
            script in `index.html` runs everywhere and wires it up.

            Which is why the copy travels as `data-` attributes: that script
            lives outside the app and cannot read the dictionary, so this is the
            one place on the site where user-facing words are handed over rather
            than rendered.

            Turnstile is loaded LAZILY by that script, on first focus. A footer
            box is on every page, and pulling the challenge script into all 248
            of them to protect a field almost nobody touches would be a tax on
            every visit — and it would multiply the orphaned-widget problem the
            tools already have.
          */}
          {/* No `border-t` on this row or the tagline row below, since
              2026-09-11 ("üzerine sadece yazıların çıktığı şekli ile full
              şekilde görebiliyor olmamız lazım"). The arc is `-z-10` inside the
              footer's stacking context, so every in-flow child paints over it —
              a full-width rule therefore drew a hard horizontal line straight
              across the artwork twice, at the two places the divider fell. The
              ribbon's own reading is a curve emerging out of nothing, and a
              ruled line across it is the one mark that contradicts that. The
              `pt-8` stays: the separation is now the whitespace alone, which is
              what the rest of the page already uses. If a divider is ever wanted
              back here, it has to stop at the text column rather than run the
              width of the container. */}
          <form
            id="nl-form"
            className="pt-8"
            data-sent={t.footer.newsletter.sent}
            data-invalid={t.footer.newsletter.invalid}
            data-failed={t.footer.newsletter.failed}
            data-checking={t.footer.newsletter.checking}
            data-blocked={t.footer.newsletter.blocked}
          >
            <p className="text-lg font-bold tracking-tight">{t.footer.newsletter.heading}</p>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              {t.footer.newsletter.sub}
            </p>
            <div className="mt-4 flex max-w-xl flex-wrap gap-3">
              <input
                id="nl-email"
                type="email"
                name="email"
                required
                autoComplete="email"
                placeholder={t.footer.newsletter.placeholder}
                className="min-w-0 flex-1 rounded-lg border border-border bg-background px-4 py-3 text-base"
              />
              <button
                id="nl-go"
                type="submit"
                className="btn-primary shrink-0 px-6 py-3"
              >
                {t.footer.newsletter.submit}
              </button>
            </div>
            {/* The honeypot. Off-screen rather than `display:none`, which some
                bots check for, and `tabIndex={-1}` so a keyboard never lands in
                it. Named `website` to match the other three forms, because the
                Worker checks that one field name. */}
            <input
              type="text"
              name="website"
              id="nl-website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="absolute left-[-9999px] h-0 w-0 opacity-0"
            />
            <div id="nl-cf" className="mt-3" />
            <p id="nl-note" className="mt-3 max-w-xl text-xs text-muted-foreground">
              {t.footer.newsletter.note}
            </p>
          </form>

          <div className="flex flex-wrap items-baseline gap-x-8 gap-y-3 pt-8">
            <p className="text-lg font-bold tracking-tight">{t.footer.tagline}</p>
            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-primary">
              {t.footer.signOff}
            </p>
            <p className="text-sm text-muted-foreground">{t.footer.offices}</p>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
            {/* The copyright line and the social boxes travel together as one
                cluster, so the bottom row keeps its two ends: this on the left,
                the legal links and the language switcher on the right. */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <p>
                © {BUILD_YEAR} {t.site.name}. {t.footer.rights}
              </p>

              {/* Small clickable boxes, at the bottom of every page — this
                  footer dresses the imported pages too, so all 154 documents
                  get them (asked for 2026-08-26: "minik tıklanabilir
                  kutucuklar").

                  The mark comes out of `content/logos.json` rather than a
                  hard-coded `/logo/instagram-118.webp`: `pnpm images` names a
                  variant after the width it actually produced, so a replacement
                  file at a different size would break every path spelled out by
                  hand. A missing manifest entry renders no box at all rather
                  than a broken image — the marks are the whole content here, so
                  a lettered fallback (what the customer wall does) would read as
                  a bug.

                  `bg-card` is white against the band, which is also what hides
                  whether a given file ships transparent or matted — the five
                  arrived as dark navy glyphs and look identical either way.

                  Opens in a new tab, which is the one place this site does that:
                  everything else it links to is its own, and sending a reader
                  off to Instagram from the last line of the page would otherwise
                  end the visit. */}
              <ul className="flex flex-wrap items-center gap-2">
                {t.footer.social.map((s) => {
                  const mark = (logos as LogoManifest)[s.icon]
                  if (!mark) return null
                  return (
                    <li key={s.url}>
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={s.label}
                        title={s.label}
                        className="flex h-10 w-10 items-center justify-center rounded-[--radius] border border-border bg-card transition-colors [transition-duration:200ms] hover:border-primary"
                      >
                        <img
                          src={mark.fallback}
                          alt=""
                          aria-hidden
                          width={mark.width}
                          height={mark.height}
                          loading="lazy"
                          decoding="async"
                          className="h-5 w-5 object-contain"
                        />
                      </a>
                    </li>
                  )
                })}
              </ul>
            </div>
            <ul className="flex flex-wrap gap-x-5 gap-y-2">
              {t.footer.legal.map((l) => (
                <li key={l.href}>
                  <a href={localizeHref(l.href, locale)} className="hover:text-primary">
                    {l.label}
                  </a>
                </li>
              ))}
              {/* Reopens the cookie bar, which lives in `index.html` outside
                  #root so it works before hydration. Written as raw HTML with an
                  inline `onclick` on purpose: 117 of the built documents never
                  hydrate, so a React `onClick` here would be a dead link on most
                  of the site. Not in `t.footer.legal` because that array is
                  {label, href} pairs and this one calls a function. */}
              <li
                dangerouslySetInnerHTML={{
                  __html:
                    '<a href="#" onclick="cookieConsent.reopen(); return false;" class="hover:text-primary">Cookie preferences</a>',
                }}
              />
            </ul>
            {/* Also in the footer, because on a phone the header hides it. It
                opens upward — there is no page left below it. */}
            <LanguageSwitcher alternates={languages} direction="up" />
          </div>
        </div>
      </footer>
    </div>
  )
}
